export const formatMoney = (value) => (value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M` : `$${Math.round(value / 1000)}k`);

const clamp = (value) => Math.min(1, Math.max(0, value));

export const statsForJob = (data, job) => {
  if (!data) return { stats: null, scope: null };
  const peers = data.pay_stats_by_category?.[job.category];
  if (peers) return { stats: peers, scope: job.category };
  return { stats: data.pay_stats || null, scope: data.pay_stats ? "the board" : null };
};

export const payComparison = (job, stats) => {
  const low = job.pay_annual_low;
  const high = job.pay_annual_high;
  if (!low || !high) return null;
  const mid = (low + high) / 2;
  const range = stats && stats.high > stats.low ? stats.high - stats.low : null;
  const diff = stats ? (mid - stats.median) / stats.median : null;
  const tone = diff === null ? "around" : diff > 0.1 ? "above" : diff < -0.1 ? "below" : "around";
  return {
    low, high, mid, diff, tone,
    color: tone === "above" ? "#0FBF77" : tone === "below" ? "#D28A1D" : "#0F141B",
    word: tone === "above" ? "Above" : tone === "below" ? "Below" : "Around",
    arrow: tone === "above" ? "▲" : tone === "below" ? "▼" : "●",
    left: range ? clamp((low - stats.low) / range) : 0.2,
    right: range ? clamp((high - stats.low) / range) : 0.6,
    median: range ? clamp((stats.median - stats.low) / range) : 0.5,
    annual: `${formatMoney(low)}${high !== low ? `–${formatMoney(high)}` : ""}/yr`,
  };
};

export const PayBar = ({ job, data, variant = "compact", testId = "pay-bar" }) => {
  const { stats, scope } = statsForJob(data, job);
  const comparison = payComparison(job, stats);
  if (!comparison) {
    return variant === "compact"
      ? <span data-testid={`${testId}-undisclosed`} className="text-[11px] italic text-[#8A94A2]">Pay not disclosed</span>
      : <p data-testid={`${testId}-undisclosed`} className="text-sm italic text-[#8A94A2]">The employer has not disclosed pay for this role. Ask about the range early in the process.</p>;
  }
  const { color, word, arrow, left, right, median, annual, diff } = comparison;
  const width = Math.max(0.02, right - left);
  const peerWord = scope && scope !== "the board" ? "peers" : "median";
  const Track = ({ height, showAxis }) => (
    <div className="relative" style={{ paddingTop: showAxis ? 4 : 0 }}>
      <div className="relative w-full bg-[#E3E5E9]" style={{ height }} aria-hidden="true">
        <span className="absolute top-0 bottom-0" style={{ left: `${left * 100}%`, width: `${width * 100}%`, background: color }} />
        {stats && <span className="absolute w-0.5 bg-[#0F141B]" style={{ left: `calc(${median * 100}% - 1px)`, top: -3, bottom: -3 }} />}
      </div>
      {showAxis && stats && (
        <div className="relative mt-1.5 h-4 font-mono-label text-[10px] !tracking-normal text-[#71747B]">
          <span className="absolute left-0">{formatMoney(stats.low)}</span>
          <span className="absolute -translate-x-1/2 text-[#0F141B] whitespace-nowrap" style={{ left: `${Math.min(0.8, Math.max(0.2, median)) * 100}%` }}>median {formatMoney(stats.median)}</span>
          <span className="absolute right-0">{formatMoney(stats.high)}</span>
        </div>
      )}
    </div>
  );

  if (variant === "compact") {
    return (
      <div data-testid={testId} className="min-w-0 w-full max-w-[220px]">
        <div className="flex items-baseline justify-between gap-2 text-[11px] leading-none mb-1.5">
          <span data-testid={`${testId}-amount`} className="font-semibold text-[#0F141B] truncate">{job.pay_period === "hour" ? `${job.pay} · ≈${annual}` : annual}</span>
          {stats && <span data-testid={`${testId}-comparison`} className="shrink-0 font-mono-label text-[9px] !tracking-normal" style={{ color }} title={`${word} the ${scope} median`}>{arrow} {word} {peerWord}</span>}
        </div>
        <Track height={6} showAxis={false} />
      </div>
    );
  }
  return (
    <div data-testid={testId} className="border hairline bg-white p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <div>
          <p data-testid={`${testId}-amount`} className="font-display text-xl font-extrabold text-[#0F141B] tracking-tight leading-none">{job.pay}</p>
          {job.pay_period === "hour" && <p className="mt-1 text-[11px] text-[#5F6168]">≈ {annual} on a full-time year</p>}
        </div>
        {stats && <p data-testid={`${testId}-comparison`} className="font-mono-label text-[11px] !tracking-normal" style={{ color }}>{arrow} {word} the {scope} median{diff !== null ? ` (${diff >= 0 ? "+" : ""}${Math.round(diff * 100)}%)` : ""}</p>}
      </div>
      <Track height={10} showAxis />
      <p className="mt-3 text-[11px] text-[#71747B]">{stats ? `Compared with ${stats.count} ${scope === "the board" ? "listings on this board" : `${scope} listings`} that disclose pay. The bar spans the 2nd–98th percentile of that group; the tick is its median. Employer-stated figures, not verified by DCYB.` : "Employer-stated figure, not verified by DCYB."}</p>
    </div>
  );
};
